/**
 * Remote audio playback.
 *
 * Video tiles are attached by the UI; audio has no tile, so the provider owns a
 * hidden sink. Browsers also block autoplay until a gesture — when LiveKit
 * reports playback blocked we retry on the very next user interaction instead
 * of leaving the user in a silent meeting with no explanation.
 *
 * SPEAKER SELECTION IS APPLIED HERE, not merely remembered. An earlier wave
 * stored the chosen `deviceId` and never called `setSinkId`, so picking a
 * headset changed nothing audible. Two things fix that for good: the id is
 * pushed onto every element that already exists, AND onto every element created
 * afterwards — someone who joins later must not land on the laptop speakers.
 */
import { RoomEvent, Track, type RemoteTrack, type Room } from 'livekit-client';

export interface AudioSink {
  add(track: RemoteTrack): void;
  remove(track: RemoteTrack): void;
  setOutput(deviceId: string): Promise<void>;
  stop(): void;
}

const AUDIO_SOURCES: readonly Track.Source[] = [
  Track.Source.Microphone,
  Track.Source.ScreenShareAudio,
];

/** `setSinkId` is Chromium-only; Safari and Firefox follow the OS default. */
type SinkCapableElement = HTMLMediaElement & { setSinkId(deviceId: string): Promise<void> };

const canSetSink = (element: HTMLMediaElement): element is SinkCapableElement =>
  'setSinkId' in element && typeof Reflect.get(element, 'setSinkId') === 'function';

export function speakerSelectionSupported(): boolean {
  return typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
}

export function createAudioSink(room: Room): AudioSink {
  const container = document.createElement('div');
  container.dataset['cyphexAudioSink'] = 'true';
  container.style.display = 'none';
  document.body.append(container);
  const elements = new Map<string, HTMLMediaElement>();
  let outputDeviceId = '';

  const applySink = (element: HTMLMediaElement): void => {
    if (outputDeviceId.length === 0 || !canSetSink(element)) return;
    void element.setSinkId(outputDeviceId).catch(() => undefined);
  };

  return {
    add(track) {
      if (track.kind !== Track.Kind.Audio || !AUDIO_SOURCES.includes(track.source)) return;
      const key = track.sid ?? track.mediaStreamID;
      if (elements.has(key)) return;
      const element = track.attach();
      element.autoplay = true;
      container.append(element);
      elements.set(key, element);
      applySink(element);
    },

    remove(track) {
      const key = track.sid ?? track.mediaStreamID;
      const element = elements.get(key);
      if (!element) return;
      track.detach(element);
      element.remove();
      elements.delete(key);
    },

    async setOutput(deviceId) {
      outputDeviceId = deviceId;
      // LiveKit records the choice so tracks it attaches internally follow it…
      await room.switchActiveDevice('audiooutput', deviceId).catch(() => undefined);
      // …and this is what actually moves the audio that is playing right now.
      for (const element of elements.values()) applySink(element);
    },

    stop() {
      for (const element of elements.values()) element.remove();
      elements.clear();
      container.remove();
    },
  };
}

/** Retries blocked playback on the next gesture. Returns an unsubscribe fn. */
export function watchAudioPlayback(room: Room): () => void {
  const GESTURES: readonly string[] = ['pointerdown', 'keydown'];
  let pending = false;

  const resume = (): void => {
    pending = false;
    for (const gesture of GESTURES) document.removeEventListener(gesture, resume);
    void room.startAudio().catch(() => undefined);
  };

  const onPlaybackChanged = (): void => {
    if (room.canPlaybackAudio || pending) return;
    pending = true;
    for (const gesture of GESTURES) document.addEventListener(gesture, resume, { once: true });
  };

  room.on(RoomEvent.AudioPlaybackStatusChanged, onPlaybackChanged);
  return () => {
    room.off(RoomEvent.AudioPlaybackStatusChanged, onPlaybackChanged);
    if (pending) for (const gesture of GESTURES) document.removeEventListener(gesture, resume);
  };
}
